import { apiService } from '@/shared/services/apiServices';
import { CERTIFICATE_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  CertificateTemplateDetail,
  CertificateTemplateFormReference,
  CertificateTemplateSelectedFormField,
  CertificateTemplateUserRef,
} from '../types';
import certificateTemplateEndpoints from './certificateTemplateEndpoints';

export interface GetCertificateTemplateResult {
  success: boolean;
  data?: CertificateTemplateDetail;
  error?: string;
  notFound?: boolean;
}

const pickString = (
  raw: Record<string, unknown>,
  ...keys: string[]
): string => {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string') {
      return v;
    }
  }
  return '';
};

const pickNullableString = (
  raw: Record<string, unknown>,
  ...keys: string[]
): string | null => {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string') {
      return v;
    }
    if (v === null) {
      return null;
    }
  }
  return null;
};

const pickBoolean = (
  raw: Record<string, unknown>,
  ...keys: string[]
): boolean => {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'boolean') {
      return v;
    }
  }
  return false;
};

const normalizeFormTemplate = (
  raw: unknown
): CertificateTemplateFormReference | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  return {
    id: pickString(obj, 'id'),
    formTemplateId: pickString(obj, 'formTemplateId', 'form_template_id'),
    name: pickString(obj, 'name'),
  };
};

const normalizeSelectedFormFields = (
  raw: unknown
): CertificateTemplateSelectedFormField[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is Record<string, unknown> =>
        !!entry && typeof entry === 'object'
    )
    .map((entry) => ({
      id: pickString(entry, 'id'),
      label: pickString(entry, 'label'),
      type: pickString(entry, 'type') || 'text',
    }));
};

const normalizeUserRef = (raw: unknown): CertificateTemplateUserRef | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  return {
    userId: pickString(obj, 'userId', 'user_id'),
    name: pickString(obj, 'name'),
    profileUrl: pickNullableString(obj, 'profileUrl', 'profile_url'),
  };
};

const normalizeCertificateTemplate = (
  raw: Record<string, unknown>
): CertificateTemplateDetail => ({
  id: pickString(raw, 'id'),
  certTemplateId: pickString(raw, 'certTemplateId', 'cert_template_id'),
  certTemplateName: pickString(raw, 'certTemplateName', 'cert_template_name'),
  headerField: pickString(raw, 'headerField', 'header_field'),
  footerField: pickString(raw, 'footerField', 'footer_field'),
  subtitle: pickNullableString(raw, 'subtitle'),
  includeTechnicianDetail: pickBoolean(
    raw,
    'includeTechnicianDetail',
    'include_technician_detail'
  ),
  formTemplate: normalizeFormTemplate(
    raw['formTemplate'] ?? raw['form_template']
  ),
  selectedFormFields: normalizeSelectedFormFields(
    raw['selectedFormFields'] ?? raw['selected_form_fields']
  ),
  createdBy: normalizeUserRef(raw['createdBy'] ?? raw['created_by']),
  updatedBy: normalizeUserRef(raw['updatedBy'] ?? raw['updated_by']),
  createdAt: pickString(raw, 'createdAt', 'created_at'),
  updatedAt: pickString(raw, 'updatedAt', 'updated_at'),
});

export async function getCertificateTemplate(
  id: string
): Promise<GetCertificateTemplateResult> {
  try {
    const url = `${certificateTemplateEndpoints.LIST}/${id}`;
    const result = await apiService.getAPI(url);

    if (!result.error) {
      // Backend may return the template flat or wrapped in `{ data: {...} }`.
      const payload = (result.data ?? {}) as Record<string, unknown>;
      const inner =
        payload['data'] && typeof payload['data'] === 'object'
          ? (payload['data'] as Record<string, unknown>)
          : payload;
      return {
        success: true,
        data: normalizeCertificateTemplate(inner),
      };
    }

    if (result.status === 404) {
      return {
        success: false,
        notFound: true,
        error: CERTIFICATE_ERROR_MESSAGES.DEFAULT_ERROR,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: CERTIFICATE_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    if (result.status === 403) {
      return {
        success: false,
        error: CERTIFICATE_ERROR_MESSAGES.UNAUTHORIZED,
      };
    }

    return { success: false, error: CERTIFICATE_ERROR_MESSAGES.DEFAULT_ERROR };
  } catch {
    return {
      success: false,
      error: CERTIFICATE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
}
